/**
 * Why a sticker upload failed — the ONE vocabulary the backend, the Mini App's
 * retry policy and its copy all speak.
 *
 * The pipeline used to collapse every failure into a bare "Failed": a corrupt
 * file, a 429 from the per-user limiter and a dropped connection were
 * indistinguishable, so the client could neither retry the ones worth retrying
 * nor stop retrying the ones that never would. Two things follow from that and
 * both live here, next to each other, so they cannot drift apart:
 *
 *   1. whether a reason is worth another attempt ({@link isRetryableMediaFailure})
 *   2. what the user is told ({@link mediaFailureCopyKey})
 *
 * The reason is structured and testable; the COPY is deliberately vague. A user
 * never sees a decoder message, a status code or a stack — only a short
 * sentence about their image.
 */

export const MEDIA_FAILURE_REASONS = [
  /** The bytes are not a decodable image (truncated, corrupt, mislabelled). */
  'UNREADABLE',
  /** A real image, but not a format the pipeline accepts. */
  'UNSUPPORTED_FORMAT',
  /** The raw upload is larger than the backend will even read. */
  'FILE_TOO_LARGE',
  /** Decoded fine, but cannot be brought under Telegram's sticker byte cap. */
  'TOO_DETAILED',
  /** The request never completed at the transport level. */
  'NETWORK',
  /** The request was still outstanding when the client's deadline expired. */
  'TIMEOUT',
  /** Server overload or an unhandled 5xx — the request itself was fine. */
  'SERVER_BUSY',
  /** The per-user upload limiter rejected it; it will pass again shortly. */
  'RATE_LIMITED',
  /** Processed correctly but could not be written to storage or the database. */
  'STORAGE',
  /** The upload was abandoned deliberately (navigation, cancel). */
  'CANCELLED',
  /** Anything unclassified. Treated as permanent so it cannot loop forever. */
  'INTERNAL',
] as const;

export type MediaFailureReason = (typeof MEDIA_FAILURE_REASONS)[number];

export function isMediaFailureReason(value: unknown): value is MediaFailureReason {
  return typeof value === 'string' && (MEDIA_FAILURE_REASONS as readonly string[]).includes(value);
}

/**
 * Reasons another attempt could plausibly fix.
 *
 * The list is a CLOSED allow-list rather than a deny-list: an unrecognised
 * reason is permanent by default, so a new failure mode can never quietly start
 * consuming the retry budget on every image in a 100-file batch.
 *
 * `CANCELLED` is deliberately absent — the request was abandoned on purpose,
 * and resurrecting it is not the retry policy's decision.
 */
const RETRYABLE: ReadonlySet<MediaFailureReason> = new Set<MediaFailureReason>([
  'NETWORK',
  'TIMEOUT',
  'SERVER_BUSY',
  'RATE_LIMITED',
  'STORAGE',
]);

export function isRetryableMediaFailure(reason: MediaFailureReason): boolean {
  return RETRYABLE.has(reason);
}

/**
 * The i18n key describing a reason to the user.
 *
 * Several distinct internal reasons intentionally share one sentence: the user
 * can act on "we couldn't read this image", but not on the difference between a
 * truncated PNG and an unsupported colour profile. The precise reason stays on
 * the item for diagnostics and tests.
 */
export function mediaFailureCopyKey(reason: MediaFailureReason): string {
  switch (reason) {
    case 'UNREADABLE':
    case 'UNSUPPORTED_FORMAT':
      return 'stickers.failure.unreadable';
    case 'FILE_TOO_LARGE':
      return 'stickers.failure.tooLarge';
    case 'TOO_DETAILED':
      return 'stickers.failure.tooDetailed';
    case 'CANCELLED':
      return 'stickers.failure.cancelled';
    // Everything retryable shares one sentence once the retries are spent:
    // by then the distinction between a 429 and a 503 helps nobody.
    default:
      return 'stickers.failure.temporary';
  }
}

/** The key shown WHILE automatic retries are still in flight. */
export const MEDIA_RETRYING_COPY_KEY = 'stickers.failure.retrying';

/**
 * Classify an HTTP failure into a reason.
 *
 * `reason` is the server's OWN classification, passed through the error
 * envelope's `details`, and it always wins — the backend knows whether a 422
 * meant "undecodable" or "too detailed". The status code is only the fallback
 * for errors raised before any handler ran (a proxy 502, the limiter's 429) and
 * for a transport failure, which has no status at all.
 */
export function classifyMediaFailure(input: {
  status: number;
  code?: string;
  reason?: unknown;
}): MediaFailureReason {
  if (isMediaFailureReason(input.reason)) return input.reason;
  if (input.code === 'TIMEOUT') return 'TIMEOUT';
  if (input.code === 'CANCELLED') return 'CANCELLED';
  // Status 0 is this client's marker for "the request never produced a
  // response" — DNS, TLS, a reset connection, or a suspended WebView.
  if (input.status === 0 || input.code === 'NETWORK') return 'NETWORK';
  if (input.status === 429) return 'RATE_LIMITED';
  if (input.status === 408 || input.status === 504) return 'TIMEOUT';
  if (input.status >= 500) return 'SERVER_BUSY';
  if (input.status === 413) return 'FILE_TOO_LARGE';
  if (input.status === 415) return 'UNSUPPORTED_FORMAT';
  if (input.status >= 400) return 'UNREADABLE';
  return 'INTERNAL';
}
